import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '@/services/api';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  site_ids: string[];
  permissions: string[];
  avatar?: string;
  /** Tenant the user belongs to. Returned by /auth/me and
   *  /auth/verify-email. Used by the onboarding wizard to scope its API
   *  calls and localStorage state. */
  organizationId?: string | null;
  onboardingCompleted?: boolean | null;

  // SS-SEC-024: support-session impersonation fields. is_global gates the
  // ability to initiate a session (renders the /admin/support-session entry
  // page). The three impersonated* fields are populated only when the current
  // JWT carries impersonation claims — they drive the persistent banner.
  is_global?: boolean;
  impersonatedBy?: string | null;
  impersonatedOrgId?: string | null;
  supportSessionId?: string | null;
}

type BackendUser = User & { sites?: { id: string }[] };

// Auth persistence:
// Browser auth is cookie-only. The httpOnly cookie authorizes API calls and is
// intentionally unreadable from JavaScript. localStorage keeps non-secret user
// metadata only so the shell can bootstrap after a hard refresh; stale legacy
// token keys are removed on load.

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  authChecked: boolean;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  mfaRequired: boolean;
  mfaUserId: string | null;
  mfaTempToken: string | null;
}

const LEGACY_ACCESS_TOKEN_KEY = 'auth_token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

function removeStoredKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage can throw in private mode / quota issues — ignore.
  }
}

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: User | null): void {
  try {
    if (user === null) {
      localStorage.removeItem(USER_KEY);
    } else {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  } catch {
    // ignore
  }
}

function clearStoredAuth(): void {
  removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
  removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
  writeStoredUser(null);
}

removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);

const initialUser = readStoredUser();

const initialState: AuthState = {
  user: initialUser,
  token: null,
  refreshToken: null,
  // Persisted user metadata is not proof of a valid httpOnly cookie session.
  // Protected routes prove the browser session with /auth/me before rendering.
  isAuthenticated: false,
  authChecked: false,
  loading: false,
  error: null,
  errorCode: null,
  mfaRequired: false,
  mfaUserId: null,
  mfaTempToken: null,
};

function mapBackendUser(rawUser: BackendUser): User {
  return {
    ...rawUser,
    site_ids: rawUser.sites?.map((s) => s.id) ?? rawUser.site_ids ?? [],
  };
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isUserLike(value: unknown): value is BackendUser {
  if (!isObjectRecord(value)) return false;
  return 'id' in value || 'email' in value;
}

function extractUserFromAuthResponse(response: unknown): BackendUser | null {
  if (!isObjectRecord(response)) return null;

  const data = response.data;
  const candidate =
    response.user ??
    (isObjectRecord(data) ? data.user ?? data : undefined) ??
    response;

  return isUserLike(candidate) ? candidate : null;
}

function buildAuthError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string; code?: string } | undefined;
    const authError = new Error(data?.error || data?.message || error.message) as Error & {
      code?: string;
    };
    if (data?.code) authError.code = data.code;
    return authError;
  }
  return error instanceof Error ? error : new Error('Authentication failed');
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    try {
      const response = await api.login(email, password);
      return response;
    } catch (error) {
      throw buildAuthError(error);
    }
  }
);

export const verifyMfaLogin = createAsyncThunk(
  'auth/mfaLoginVerify',
  async (payload: { userId: string; tempToken: string; code: string }) => {
    try {
      const response = await api.verifyMfaLogin(payload);
      return response;
    } catch (error) {
      throw buildAuthError(error);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('[auth/logout] API call failed; clearing local session anyway:', error);
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async () => {
    const response = await api.refreshToken();
    return response;
  }
);

export const bootstrapSession = createAsyncThunk(
  'auth/bootstrapSession',
  async () => {
    try {
      const response = await api.getCurrentUser();
      const user = extractUserFromAuthResponse(response);
      if (!user) {
        throw new Error('Invalid auth session response');
      }
      return { user };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          await api.refreshToken();
          const retryResponse = await api.getCurrentUser();
          const retryUser = extractUserFromAuthResponse(retryResponse);
          if (!retryUser) {
            throw new Error('Invalid auth session response');
          }
          return { user: retryUser };
        } catch (refreshError) {
          throw buildAuthError(refreshError);
        }
      }
      throw buildAuthError(error);
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, _action: PayloadAction<{ token?: string; refreshToken?: string }>) => {
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = true;
      state.authChecked = false;
      state.errorCode = null;
      removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
      removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      state.mfaRequired = false;
      state.mfaUserId = null;
      state.mfaTempToken = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.authChecked = true;
      state.error = null;
      state.errorCode = null;
      state.mfaRequired = false;
      state.mfaUserId = null;
      state.mfaTempToken = null;
      clearStoredAuth();
    },
    clearMfaChallenge: (state) => {
      state.mfaRequired = false;
      state.mfaUserId = null;
      state.mfaTempToken = null;
      state.error = null;
      state.errorCode = null;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.authChecked = true;
      clearStoredAuth();
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        writeStoredUser(state.user);
      }
    },
    clearAuthError: (state) => {
      state.error = null;
      state.errorCode = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token?: string; refreshToken?: string }>) => {
      const { user } = action.payload;
      state.user = mapBackendUser(user as User & { sites?: { id: string }[] });
      writeStoredUser(state.user);
      state.token = null;
      state.refreshToken = null;
      removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
      removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      state.isAuthenticated = true;
      state.authChecked = false;
      state.loading = false;
      state.error = null;
      state.errorCode = null;
      state.mfaRequired = false;
      state.mfaUserId = null;
      state.mfaTempToken = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data?.mfaRequired) {
          state.user = null;
          state.token = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          state.authChecked = true;
          state.error = null;
          state.errorCode = null;
          state.mfaRequired = true;
          state.mfaUserId = action.payload.data.userId ?? null;
          state.mfaTempToken = action.payload.data.tempToken ?? null;
          clearStoredAuth();
          return;
        }

        // Backend returns sites: [{ id, name }] but User type expects site_ids: string[]
        const rawUser = action.payload.data.user;
        state.user = mapBackendUser(rawUser);
        writeStoredUser(state.user);
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = true;
        state.authChecked = false;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
        removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
        state.errorCode = action.error.code ?? null;
        state.isAuthenticated = false;
        state.authChecked = true;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
      });

    // MFA login verification
    builder
      .addCase(verifyMfaLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(verifyMfaLogin.fulfilled, (state, action) => {
        state.loading = false;
        const rawUser = action.payload.data.user;
        state.user = mapBackendUser(rawUser);
        writeStoredUser(state.user);
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = true;
        state.authChecked = false;
        state.error = null;
        state.errorCode = null;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
        removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      })
      .addCase(verifyMfaLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'MFA verification failed';
        state.errorCode = action.error.code ?? null;
        state.isAuthenticated = false;
        state.authChecked = true;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.authChecked = true;
        state.error = null;
        state.errorCode = null;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        clearStoredAuth();
      });

    // Refresh rotates httpOnly cookies only. There are no browser-readable
    // tokens to persist; keep the current user metadata and mark the session
    // authenticated if the refresh succeeds.
    builder
      .addCase(refreshAccessToken.fulfilled, (state) => {
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = true;
        state.authChecked = true;
        state.errorCode = null;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
        removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      })
      .addCase(refreshAccessToken.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.authChecked = true;
        state.errorCode = null;
        state.mfaRequired = false;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        clearStoredAuth();
      });

    // Browser-session bootstrap from the httpOnly access cookie.
    builder
      .addCase(bootstrapSession.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        state.loading = false;
        state.authChecked = true;
        state.user = mapBackendUser(action.payload.user);
        writeStoredUser(state.user);
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = true;
        state.mfaRequired = false;
        state.errorCode = null;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        removeStoredKey(LEGACY_ACCESS_TOKEN_KEY);
        removeStoredKey(LEGACY_REFRESH_TOKEN_KEY);
      })
      .addCase(bootstrapSession.rejected, (state) => {
        state.loading = false;
        state.authChecked = true;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.mfaRequired = false;
        state.errorCode = null;
        state.mfaUserId = null;
        state.mfaTempToken = null;
        clearStoredAuth();
      });
  },
});

export const {
  setToken,
  clearAuth,
  clearMfaChallenge,
  updateUser,
  clearAuthError,
  setCredentials
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthChecked = (state: { auth: AuthState }) => state.auth.authChecked;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthErrorCode = (state: { auth: AuthState }) => state.auth.errorCode;
export const selectMfaRequired = (state: { auth: AuthState }) => state.auth.mfaRequired;
export const selectUserRole = (state: { auth: AuthState }) => state.auth.user?.role;
export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.user?.permissions || [];
export const selectUserFacilityIds = (state: { auth: AuthState }) => state.auth.user?.site_ids || [];
/** @deprecated Use selectUserFacilityIds */
export const selectUserSites = selectUserFacilityIds;

// SS-SEC-024 selectors.
export const selectIsGlobalUser = (state: { auth: AuthState }) => state.auth.user?.is_global === true;
export const selectIsImpersonating = (state: { auth: AuthState }) =>
  !!(state.auth.user?.impersonatedBy && state.auth.user?.impersonatedOrgId && state.auth.user?.supportSessionId);
export const selectSupportSessionId = (state: { auth: AuthState }) => state.auth.user?.supportSessionId ?? null;
